import classNames from 'classnames';
import { ClearIndicator } from 'components/shared/Tags/clear-indicator';
import { TagsPicker } from 'components/shared/Tags/tags-picker';
import { MultiValueContainer } from 'components/shared/underlyings/selector/multi/multi-value-container';
import { MenuList } from 'components/shared/underlyings/selector/result/menu-list';
import { NoOptionsMessage } from 'components/shared/underlyings/selector/result/no-option-message';
import { Option } from 'components/shared/underlyings/selector/result/option';
import { UnderlyingHeader } from 'components/shared/underlyings/underlyings-card/header';
import { useGenerateUnderlyingsGridForExcel } from 'hooks/underlyings/use-generate-underlyings-grid-for-excel';
import { useEssentialUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import { useUnderlyingsBasket } from 'hooks/underlyings/use-underlyings-basket';
import { useUnderlyingsTags } from 'hooks/underlyings/use-underlyings-tags';
import { useCopyToExcel } from 'hooks/useCopyToExcel';

import './style.scss';
import { Underlying } from 'models/underlying';
import { UnderlyingCodeType } from 'models/underlying/base';
import { useIntl } from 'react-intl';

import { Result } from './result';

export type UnderlyingsCardProps = {
  underlyings: ReadonlyArray<Underlying>;
  setUnderlyings: (underlyings: ReadonlyArray<Underlying>) => void;
  formId: string;
  underlyingBlock: string;
  placeholder?: string;
  defaultUnderlyingCodeType?: UnderlyingCodeType;
  tabId?: string;
  isUnderlyingResultExpanded: boolean;
  isInvalid?: boolean;
  isHighlighted?: boolean;
  onToggleUnderlyingResult: () => void;
};

const DEFAULT_UNDERLYING_CODE_TYPE = 'Bloomberg' as const;

export function UnderlyingsCard({
  underlyings,
  setUnderlyings,
  formId,
  underlyingBlock,
  placeholder,
  defaultUnderlyingCodeType,
  isUnderlyingResultExpanded,
  onToggleUnderlyingResult,
  isInvalid = false,
  isHighlighted = false,
}: UnderlyingsCardProps) {
  const { formatMessage } = useIntl();
  const { search, getOptionsFromPastedLabels } = useEssentialUnderlyingSearchByFormId(formId, underlyingBlock);
  const {
    onTagRemoved,
    onTagsChanged,
    tags,
    newValidTags,
    clearAllTags,
    updateUnderlyings,
    onUnderlyingRemoved,
    hasTags,
  } = useUnderlyingsTags({
    underlyings,
    isMulti: true,
    onChange: setUnderlyings,
    codeType: defaultUnderlyingCodeType ?? DEFAULT_UNDERLYING_CODE_TYPE,
    removeInvalidTagsOnUnderlyingsChange: true,
    underlyingsSyncMode: 'merge',
  });
  const { selectedUnderlyings, selectedUnderlyingsIds } = useUnderlyingsBasket({ underlyings, tags });
  const { generateTableExport } = useGenerateUnderlyingsGridForExcel(underlyings);
  const onCopyAll = useCopyToExcel(generateTableExport);

  return (
    <div
      className={classNames(
        'card card-bordering-primary px-12px pb-2 pt-1 bg-lvl1 d-flex flex-column underlyings-card-container',
        {
          'border-bottom border-warning': isInvalid,
          'border-bottom border-info': isHighlighted,
        },
      )}
    >
      <UnderlyingHeader
        labelKey="form.Underlyings"
        clearDisabled={!hasTags}
        resultShown={isUnderlyingResultExpanded}
        onClear={() => clearAllTags(true)}
        onCopyAll={onCopyAll}
        onToggleResult={onToggleUnderlyingResult}
        isCopyButtonVisible={underlyings.length > 0}
      />
      <div className="d-flex align-items-center mt-2" onBlur={updateUnderlyings}>
        <i className="icon icon-sm text-secondary">search</i>
        <TagsPicker<Underlying>
          className="h-auto"
          components={{
            MenuList,
            Option,
            NoOptionsMessage,
            ClearIndicator: ClearIndicator.Default,
            MultiValueContainer: p => <MultiValueContainer onTagRemoved={onTagRemoved} {...p} />,
          }}
          isMulti
          convertPastedLabelsToTags={inputs =>
            getOptionsFromPastedLabels(
              inputs,
              selectedUnderlyingsIds,
              defaultUnderlyingCodeType ?? DEFAULT_UNDERLYING_CODE_TYPE,
            )
          }
          filterTags={input =>
            search(
              input,
              selectedUnderlyingsIds,
              selectedUnderlyings,
              defaultUnderlyingCodeType ?? DEFAULT_UNDERLYING_CODE_TYPE,
            )
          }
          id="underlyings"
          isClearable={false}
          isMenuListNarrow
          onTagsChanged={t => onTagsChanged(t, false)}
          placeholder={placeholder ?? formatMessage({ id: 'underlying.placeholder.long' })}
          tags={tags}
        />
        <button
          data-e2e="add-underlyings-button"
          className="btn btn-sm btn-light me-1"
          disabled={newValidTags.length === 0}
        >
          <i className="icon icon-sm">done</i>
        </button>
      </div>

      {isUnderlyingResultExpanded && (
        <Result
          underlyings={underlyings}
          onUnderlyingsChanged={setUnderlyings}
          onUnderlyingRemoved={t => onUnderlyingRemoved(t, true)}
        />
      )}
    </div>
  );
}
